<template>
  <div class="station-manage-container">
    <Container title="站点管理">
      <!-- ★ 新增：最外层 flex 容器 -->
      <div class="flex-page-layout">
        <!-- 搜索卡片 -->
        <el-card shadow="never" class="search-card">
          <el-form :model="searchForm" label-position="left" label-width="70px" @submit.prevent @keyup.enter="handleSearch">
            <el-row :gutter="20">
              <el-col :xs="24" :sm="12" :md="6" :lg="6" v-if="isSuperAdmin">
                <el-form-item label="企业">
                  <el-select v-model="searchForm.enterpriseId" placeholder="请选择企业" clearable style="width: 100%;" @change="handleEnterpriseChange">
                    <el-option v-for="item in enterpriseList" :key="item.id" :label="item.name" :value="item.id" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="6" :lg="6" v-if="isSuperAdmin">
                <el-form-item label="站点">
                  <el-select v-model="searchForm.stationId" placeholder="请选择站点" clearable style="width: 100%;" :disabled="!searchForm.enterpriseId">
                    <el-option v-for="item in stationOptions" :key="item.id" :label="item.name" :value="item.id" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="6" :lg="6">
                <el-form-item label="时间">
                  <el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至" start-placeholder="开始日期" 
                  end-placeholder="结束日期" value-format="YYYY-MM-DD" :shortcuts="shortcuts" style="width: 100%;" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="6" :lg="6" style="display: flex; justify-content: flex-end;">
                <el-form-item label=" " label-width="0">
                  <div style="display: flex; gap: 8px;">
                    <el-button type="primary" @click="handleSearch">查询</el-button>
                    <el-button @click="resetSearch">重置</el-button>
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </el-card>

        <!-- 表格卡片 -->
        <el-card shadow="never" class="table-card">
          <template #header>
            <div class="table-header">
              <div class="table-header-left">
                <el-button v-if="$hasPermission('btn:add_station')" type="primary" @click="openAddDialog">
                  <el-icon><Plus /></el-icon> 添加
                </el-button>
              </div>
              <div class="table-header-right">
                <el-tooltip content="刷新" placement="bottom">
                  <el-button circle @click="fetchData"><el-icon><Refresh /></el-icon></el-button>
                </el-tooltip>
                <el-tooltip content="导出" placement="bottom">
                  <el-button circle @click="handleExport"><el-icon><Download /></el-icon></el-button>
                </el-tooltip>
                <el-tooltip content="打印" placement="bottom">
                  <el-button circle @click="handlePrint"><el-icon><Printer /></el-icon></el-button>
                </el-tooltip>
              </div>
            </div>
          </template>
          
          <!-- ★ 新增：表格包裹层，替换原有的 table-wrapper -->
          <div class="table-container">
            <el-table
              :data="tableData"
              border
              style="width: 100%; min-width: 1200px;"
              :header-cell-style="{ 'text-align': 'center' }"
              height="100%"
            >
              <el-table-column prop="id" label="ID" min-width="70" align="center" />
              <el-table-column prop="name" label="站点名称" min-width="140" align="center" show-overflow-tooltip />
              <el-table-column prop="available_balance" label="可提现金额" min-width="110" align="center">
                <template #default="{ row }">
                  {{ Number(row.available_balance || 0).toFixed(3) }}
                </template>
              </el-table-column>

              <el-table-column label="储备金" min-width="150" align="center">
                <template #default="{ row }">
                  <div class="reserve-cell">
                    <span class="reserve-amount" :class="{ 'negative': row.reserve < 0 }">
                      {{ (row.reserve / 100).toFixed(2) }}
                    </span>
                    <el-dropdown trigger="hover" @command="(cmd) => handleReserveCommand(row, cmd)">
                      <span class="reserve-dropdown-trigger">
                        变更
                        <svg viewBox="0 0 10 6" width="10" height="6" style="display:inline-block;vertical-align:middle;margin-left:3px;">
                          <polyline points="1,1 5,5 9,1" stroke="#409EFF" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </span>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <el-dropdown-item command="increase">新增</el-dropdown-item>
                          <el-dropdown-item command="decrease">减少</el-dropdown-item>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </div>
                </template>
              </el-table-column>

              <el-table-column prop="province" label="省" min-width="90" align="center" show-overflow-tooltip />
              <el-table-column prop="city" label="市" min-width="90" align="center" show-overflow-tooltip />
              <el-table-column prop="district" label="区/县" min-width="90" align="center" show-overflow-tooltip />
              <el-table-column prop="detail_address" label="详细地址" min-width="160" align="center" show-overflow-tooltip />
              <el-table-column prop="phone" label="电话" min-width="110" align="center" />
              <el-table-column label="状态" min-width="100" align="center">
                <template #default="{ row }">
                  <el-switch v-model="row.status" :active-value="1" :inactive-value="0" @change="toggleStatus(row)" />
                </template>
              </el-table-column>
              <el-table-column prop="created_at" label="创建时间" min-width="160" align="center">
                <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
              </el-table-column>

              <el-table-column label="二维码" min-width="200" align="center">
                <template #default="{ row }">
                  <div style="display: flex; gap: 4px; justify-content: center; flex-wrap: nowrap;">
                    <el-button size="small" type="primary" plain style="flex: 1; min-width: 0; padding: 5px 6px; font-size: 12px;" @click="handleQrCode(row)">微信二维码</el-button>
                    <el-button size="small" type="success" plain style="flex: 1; min-width: 0; padding: 5px 6px; font-size: 12px;" @click="handleAlipayCode(row)">支付宝二维码</el-button>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="操作" min-width="280" fixed="right" align="center">
                <template #default="{ row }">
                  <el-button v-if="$hasPermission('btn:edit_station')" size="small" type="warning" plain @click="openEditDialog(row)">编辑</el-button>
                  <el-button v-if="isSuperAdmin && $hasPermission('btn:view_detail')" size="small" type="success" plain @click="openDetailDialog(row)">利润明细</el-button>
                  <el-button v-if="$hasPermission('btn:open_split')" size="small" type="primary" plain disabled @click="openSplitDialog(row)">分账开通</el-button>
                  <el-popconfirm v-if="$hasPermission('btn:delete_station')" confirm-button-text="删除" cancel-button-text="取消" title="确定删除该站点吗？" @confirm="handleDelete(row)">
                    <template #reference>
                      <el-button size="small" type="danger" plain>删除</el-button>
                    </template>
                  </el-popconfirm>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="pagination-wrapper">
            <el-pagination
              v-model:current-page="page.current"
              v-model:page-size="page.pageSize"
              :total="total"
              layout="total, sizes, prev, pager, next, jumper"
              :page-sizes="[10, 40, 50, 100]"
              @size-change="onSizeChange"
              @current-change="fetchData"
            />
          </div>
        </el-card>
      </div>

      <!-- 新增/编辑弹窗 -->
      <el-dialog :title="dialogTitle" v-model="dialogVisible" width="700px" @close="closeDialog" destroy-on-close>
        <el-form :rules="rules" :model="form" label-width="140px" label-position="right" ref="formRef">
          <el-form-item label="站点名称：" prop="name" required>
            <el-input v-model="form.name" placeholder="请填写站点名称" />
          </el-form-item>
      
          <el-form-item label="站点类别：">
            <el-select v-model="form.category" placeholder="请选择站点类别" style="width: 100%">
              <el-option label="加气站" value="gas" />
              <el-option label="充电站" value="charge" />
              <el-option label="加油站" value="oil" />
            </el-select>
          </el-form-item>
      
          <el-form-item label="支付类型：">
            <el-radio-group v-model="form.paymentType">
              <el-radio :value="'all'">全部</el-radio>
              <el-radio :value="'amount'">金额</el-radio>
              <el-radio :value="'weight'">重量</el-radio>
            </el-radio-group>
          </el-form-item>
      
          <el-form-item label="支付渠道：">
            <el-radio-group v-model="form.paymentChannel">
              <el-radio :value="'yinsheng'">银盛</el-radio>
            </el-radio-group>
          </el-form-item>
      
          <el-form-item label="隶属企业：">
            <el-select v-model="form.enterpriseId" placeholder="请选择隶属企业" style="width: 100%" filterable :loading="enterpriseLoading" :disabled="!isSuperAdmin">
              <el-option v-for="item in formEnterpriseList" :key="item.id" :label="item.name" :value="Number(item.id)" />
            </el-select>
          </el-form-item>
      
          <el-form-item label="所在城市：" prop="region">
            <el-cascader v-model="form.region" :options="areaOptions" :props="cascaderProps" placeholder="请选择省/市/区" clearable style="width: 100%" @change="handleRegionChange" />
          </el-form-item>
      
          <el-form-item label="地图坐标：">
            <div style="display: flex; gap: 8px; align-items: center;">
              <el-input v-model="form.coordinates.lng" placeholder="经度" style="width: 35%" />
              <el-input v-model="form.coordinates.lat" placeholder="纬度" style="width: 35%" />
              <el-button type="primary" plain size="small" @click="openMapSelector">打开地图选择</el-button>
            </div>
          </el-form-item>
      
          <el-form-item label="详细地址：" prop="detail_address">
            <el-input v-model="form.detail_address" placeholder="请输入详细地址（街道、门牌号等）" />
          </el-form-item>
      
          <el-form-item label="联系人：">
            <el-input v-model="form.contact" placeholder="请输入联系人" />
          </el-form-item>
      
          <el-form-item label="电话：" prop="phone">
            <el-input v-model="form.phone" placeholder="请输入联系人电话" />
          </el-form-item>
      
          <el-form-item label="标签：">
            <el-checkbox-group v-model="form.tags">
              <el-checkbox v-for="item in tagOptions" :key="item.value" :value="item.value">{{ item.label }}</el-checkbox>
            </el-checkbox-group>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">可多选，用于前端展示站点特性</div>
          </el-form-item>
      
          <el-form-item label="LOGO：">
            <el-upload class="avatar-uploader" :action="uploadUrl" :on-success="handleLogoSuccess" :show-file-list="false" :headers="uploadHeaders">
              <img v-if="form.logo" :src="fileurl + form.logo.replace(/^\//, '')" class="avatar" />
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
          </el-form-item>
      
          <el-form-item label="背景图：">
            <el-upload class="avatar-uploader" :action="uploadUrl" :on-success="handleBgSuccess" :show-file-list="false" :headers="uploadHeaders">
              <img v-if="form.bgImage" :src="fileurl + form.bgImage.replace(/^\//, '')" class="avatar" />
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
          </el-form-item>
      
          <el-form-item label="备注：">
            <el-input v-model="form.remark" type="textarea" :rows="3" placeholder="请输入备注" />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="submitForm">保存</el-button>
        </template>
      </el-dialog>

      <!-- 储备金调整弹窗 -->
      <el-dialog :title="reserveDialogTitle" v-model="reserveDialogVisible" width="400px" @close="resetReserveDialog">
        <el-form :model="reserveForm" label-width="139px">
          <el-form-item :label="reserveType === 1 ? '增加金额（元）：' : '减少金额（元）：'" required>
            <el-input-number v-model="reserveForm.amount" :min="0.01" :precision="2" :step="0.1" controls-position="right" style="width: 100%" />
          </el-form-item>
          <el-form-item label="备注：">
            <el-input v-model="reserveForm.remark" placeholder="选填备注" />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="reserveDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitReserve">确定</el-button>
        </template>
      </el-dialog>

      <!-- 二维码预览弹窗 -->
      <el-dialog v-model="qrDialogVisible" :title="qrType === 'qr' ? '微信二维码' : (qrType === 'alipay' ? '支付宝二维码' : '聚合码')" width="260px" center :close-on-click-modal="false">
        <div style="display: flex; justify-content: center; align-items: center; padding: 6px 0;">
          <img :src="qrImageUrl" v-if="qrImageUrl" style="width: 160px; height: 160px; display: block; border-radius: 4px;" />
          <div v-else style="color: #999; font-size: 14px;">暂无二维码</div>
        </div>
      </el-dialog>

      <!-- 站点超管抽成明细弹窗（仅超管可见） -->
      <el-dialog :title="`超管抽成明细 - ${currentStationRow ? currentStationRow.name : ''}`" v-model="detailDialogVisible" width="55%" top="15vh" align-center destroy-on-close>
        <div style="margin-bottom: 16px;">
          <el-radio-group v-model="statType" @change="fetchDetailData">
            <el-radio-button value="3days">3天</el-radio-button>
            <el-radio-button value="7days">7天</el-radio-button>
            <el-radio-button value="30days">1个月</el-radio-button>
          </el-radio-group>
        </div>
      
        <div style="padding: 10px 0;">
          <div style="margin-bottom: 16px; display: flex; gap: 24px; flex-wrap: wrap;">
            <div><strong>总订单数：</strong>{{ totalCount }}</div>
            <div><strong>总支付金额：</strong>￥{{ totalAmount.toFixed(2) }}</div>
            <div><strong>超管利润比例：</strong>{{ superRatio }}%</div>
            <div>
              <strong>超管利润总额：</strong>
              <span style="color:#e6a23c;font-weight:bold;">￥{{ Number(totalSuperAmount).toFixed(4) }}</span>
            </div>
          </div>
          <el-table :data="orderList" border style="width: 100%;" max-height="420" empty-text="该时间段内暂无订单" v-loading="detailLoading">
            <el-table-column type="index" label="#" width="55" align="center" />
            <el-table-column prop="order_no" label="订单号" min-width="200" />
            <el-table-column prop="paid_amount" label="支付金额(元)" width="130" align="right">
              <template #default="{ row }">￥{{ Number(row.paid_amount || 0).toFixed(3) }}</template>
            </el-table-column>
            <el-table-column prop="super_admin_amount" label="超管利润(元)" width="130" align="right">
              <template #default="{ row }">
                <span style="color:#e6a23c;font-weight:bold;">￥{{ Number(row.super_admin_amount || 0).toFixed(4) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="时间" width="180">
              <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </div>
        <template #footer>
          <el-button @click="detailDialogVisible = false">关闭</el-button>
        </template>
      </el-dialog>

      <!-- 分账开通弹窗 -->
      <el-dialog title="分账开通" v-model="splitDialogVisible" width="750px" destroy-on-close>
        <el-form :model="splitForm" label-width="150px" ref="splitFormRef">
          <el-form-item label="分账类型" required>
            <el-radio-group v-model="splitForm.type" @change="onTypeChange">
              <el-radio :value="'0'">余额分账</el-radio>
              <el-radio :value="'1'" disabled>订单分账</el-radio>
            </el-radio-group>
            <div style="font-size:12px;color:#999;margin-top:4px;">类型：0-余额分账，1-订单分账</div>
          </el-form-item>
          <el-form-item label="分账场景" required>
            <el-select v-model="splitForm.scene" placeholder="请选择分账场景" style="width:100%">
              <el-option label="代售保险、加油站等特殊行业" value="00" />
              <el-option label="下级单位财政款" value="01" />
              <el-option label="网络货运平台或交通运输司机/车队劳务费" value="02115" />
              <el-option label="供应商贷款" value="03" />
              <el-option label="线上分销商/经销商/站点/加盟店应收账款" value="04" />
              <el-option label="线下分销商/经销商/站点/加盟店应收账款" value="05" />
              <el-option label="推广返佣" value="06" />
              <el-option label="系统商/平台商服务费" value="07" />
              <el-option label="平台补贴" value="08" />
              <el-option label="货主应收账款" value="09" />
              <el-option label="入驻商户应收账款" value="10" />
              <el-option label="用户应收储值款" value="11" />
              <el-option label="公司内部、集团、股东资金往来" value="12" />
              <el-option label="资方应收账款" value="13" />
              <el-option label="助贷服务费、担保费等" value="14" />
            </el-select>
          </el-form-item>
          <template v-if="splitForm.type === '1'">
            <el-form-item label="分账规则" required>
              <el-radio-group v-model="splitForm.rule" @change="onRuleChange">
                <el-radio value="1">接口动态分账</el-radio>
                <el-radio value="2" disabled>按百分比</el-radio>
                <el-radio value="3" disabled>按固定金额</el-radio>
              </el-radio-group>
            </el-form-item>
            <template v-if="splitForm.rule === '1'">
              <el-form-item label="最低分账比例(%)" required>
                <el-input-number v-model="splitForm.minRatio" :min="0" :max="100" :precision="2" style="width:180px" />
                <span style="font-size:12px;color:#999;margin-left:8px;">范围0-100，最多两位小数</span>
              </el-form-item>
              <el-form-item label="参与分账商户数" required>
                <el-input-number v-model="splitForm.maxMerchantCount" :min="1" :max="20" style="width:180px" />
                <span style="font-size:12px;color:#999;margin-left:8px;">范围1-20</span>
              </el-form-item>
            </template>
            <template v-if="splitForm.rule === '2'">
              <el-form-item label="分账明细" required>
                <el-alert title="需要提供分账明细（商户号+比例），所有比例之和必须为100%，手续费承担方最多一个" type="info" :closable="false" />
                <el-button type="primary" size="small" @click="addDetailRow">添加分账项</el-button>
                <el-table :data="splitForm.details" border style="margin-top:8px;width:100%;">
                  <el-table-column label="分账商户号" min-width="150">
                    <template #default="{ row }"><el-input v-model="row.divMercId" placeholder="请输入商户号" size="small" /></template>
                  </el-table-column>
                  <el-table-column label="比例(%)" min-width="120">
                    <template #default="{ row }"><el-input-number v-model="row.ratio" :min="0" :max="100" :precision="2" size="small" style="width:100%" /></template>
                  </el-table-column>
                  <el-table-column label="手续费承担" min-width="160">
                    <template #default="{ row }">
                      <div style="white-space:nowrap;">
                        <el-radio-group v-model="row.feeFlag" size="small">
                          <el-radio value="1">是</el-radio>
                          <el-radio value="2">否</el-radio>
                        </el-radio-group>
                        <div style="font-size:12px;color:#999;white-space:nowrap;">(不选默认:交易商户承担)</div>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="80">
                    <template #default="{ $index }"><el-button type="danger" size="mini" @click="removeDetailRow($index)">删除</el-button></template>
                  </el-table-column>
                </el-table>
              </el-form-item>
            </template>
            <template v-if="splitForm.rule === '3'">
              <el-form-item label="分账明细" required>
                <el-alert title="需要提供分账明细（商户号+金额），交易商户金额必须为0，分账接收方金额大于0" type="info" :closable="false" />
                <el-button type="primary" size="small" @click="addDetailRow">添加分账项</el-button>
                <el-table :data="splitForm.details" border style="margin-top:8px;width:100%;">
                  <el-table-column label="分账商户号" min-width="150">
                    <template #default="{ row }"><el-input v-model="row.divMercId" placeholder="请输入商户号" size="small" /></template>
                  </el-table-column>
                  <el-table-column label="金额(元)" min-width="120">
                    <template #default="{ row }"><el-input-number v-model="row.amount" :min="0" :precision="2" size="small" style="width:100%" /></template>
                  </el-table-column>
                  <el-table-column label="手续费承担" min-width="160">
                    <template #default="{ row }">
                      <div style="white-space:nowrap;">
                        <el-radio-group v-model="row.feeFlag" size="small">
                          <el-radio value="1">是</el-radio>
                          <el-radio value="2">否</el-radio>
                        </el-radio-group>
                        <div style="font-size:12px;color:#999;white-space:nowrap;">(不选默认:交易商户承担)</div>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="80">
                    <template #default="{ $index }"><el-button type="danger" size="mini" @click="removeDetailRow($index)">删除</el-button></template>
                  </el-table-column>
                </el-table>
              </el-form-item>
            </template>
          </template>
          <template v-if="splitForm.type === '0'">
            <el-form-item label="分账费率(%)" required>
              <el-input-number v-model="splitForm.rate" :min="0" :max="100" :precision="2" style="width:180px" />
              <span style="font-size:12px;color:#999;margin-left:8px;">范围0-100，最多两位小数</span>
            </el-form-item>
            <el-form-item label="费率承担方">
              <el-radio-group v-model="splitForm.feeBearer">
                <el-radio value="1">分账商户</el-radio>
                <el-radio value="2">分账接收方</el-radio>
              </el-radio-group>
              <div style="font-size:12px;color:#999;margin-top:4px;">不选默认分账商户承担</div>
            </el-form-item>
            <el-form-item label="规则来源">
              <el-radio-group v-model="splitForm.ruleSource">
                <el-radio value="1">接口发起</el-radio>
                <el-radio value="2">门户发起</el-radio>
                <el-radio value="3">接口+门户</el-radio>
              </el-radio-group>
              <div style="font-size:12px;color:#999;margin-top:4px;">不选默认门户发起</div>
            </el-form-item>
          </template>
          <el-form-item label="上传场景图片">
            <el-upload action="#" :on-change="handleSplitUpload" :file-list="splitForm.picList" list-type="picture" multiple :auto-upload="false">
              <el-button size="small" type="primary">选择图片</el-button>
            </el-upload>
            <div style="font-size:12px;color:#999;margin-top:4px;">根据分账场景上传所需的图片（如门头照、协议等），图片类型需与场景对应。</div>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="splitDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitSplit" :loading="splitSubmitting">提交</el-button>
        </template>
      </el-dialog>
      
      <!-- 地图坐标选择弹窗 -->
      <el-dialog title="选择地图坐标" v-model="mapDialogVisible" width="50%" top="5vh" destroy-on-close @close="closeMapDialog">
        <div style="display:flex; gap:8px; margin-bottom:12px;">
          <el-input v-model="searchKeyword" placeholder="输入地点名称搜索" clearable @keyup.enter="searchPlace" style="flex:1;" />
          <el-button type="primary" @click="searchPlace">搜索</el-button>
          <el-button @click="resetMapPosition">重置</el-button>
        </div>
        <div id="mapContainer" style="width:100%;height:500px;"></div>
        <div style="margin-top:16px;">
          <el-form label-width="80px" size="small">
            <el-form-item label="经度"><el-input v-model="tempLng" readonly /></el-form-item>
            <el-form-item label="纬度"><el-input v-model="tempLat" readonly /></el-form-item>
          </el-form>
        </div>
        <template #footer>
          <el-button @click="mapDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmMapSelection">确认选择</el-button>
        </template>
      </el-dialog>

      <!-- 导出弹窗 -->
      <ExportDialog
        v-model:visible="exportDialogVisible"
        :fields="exportFields"
        :export-api="exportApi"
        :get-search-params="getSearchParams"
        :current-page="page"
        :default-file-name="defaultFileName()"
      />
    </Container>
  </div>
</template>

<script>
const pca = require('china-division/dist/pca.json');

function buildAreaOptions(data) {
  if (!data || typeof data !== 'object') return [];
  const provinceList = [];
  for (const [provinceName, cities] of Object.entries(data)) {
    if (typeof cities === 'object' && cities !== null) {
      const cityList = [];
      for (const [cityName, districts] of Object.entries(cities)) {
        const children = Array.isArray(districts) ? districts.map(d => ({ name: d })) : [];
        cityList.push({ name: cityName, children: children.length > 0 ? children : undefined });
      }
      provinceList.push({ name: provinceName, children: cityList.length > 0 ? cityList : undefined });
    }
  }
  return provinceList;
}
const AREA_OPTIONS = buildAreaOptions(pca);

import ExportDialog from '@/components/container/ExportDialog.vue';
import exportMixin from '@/mixins/exportMixin';
import { pub } from '/common/event';
import moment from 'moment';
import { Plus, Refresh, Download, Printer } from '@element-plus/icons-vue';
import { dateShortcuts } from '../../../utils/shortcuts.js';
import enterpriseStationMixin from '@/mixins/enterpriseStationMixin';

export default {
  components: { Plus, Refresh, Download, Printer, ExportDialog },
  mixins: [exportMixin, enterpriseStationMixin],
  data() {
    return {
      shortcuts: dateShortcuts,
      searchForm: { enterpriseId: null, stationId: null, dateRange: null },
      formEnterpriseList: [],
      tableData: [],
      total: 0,
      page: { current: 1, pageSize: 10 },
      dialogVisible: false,
      dialogTitle: '添加站点',
      editId: null,
      enterpriseLoading: false,
      form: {
        name: '', category: '', paymentType: 'all', paymentChannel: 'yinsheng',
        enterpriseId: null, region: [], province: '', city: '', district: '',
        coordinates: { lng: '', lat: '' }, detail_address: '', contact: '', phone: '',
        tags: [], logo: '', bgImage: '', remark: '', oil92: 0, oil95: 0, oil98: 0, oil0: 0,
        commission_rate: 0, status: 1,
      },
      tagOptions: [
        { label: '优选站点', value: '优选站点' },
        { label: '加气速度快', value: '加气速度快' },
        { label: '排队少', value: '排队少' },
        { label: '支付方式多', value: '支付方式多' },
        { label: '服务好', value: '服务好' },
        { label: '有积分', value: '有积分' },
        { label: '定期维护', value: '定期维护' },
        { label: '好评多', value: '好评多' },
        { label: '优惠大', value: '优惠大' },
        { label: '不排队', value: '不排队' },
        { label: '24小时营业', value: '24小时营业' },
        { label: '有便利店', value: '有便利店' },
        { label: '有卫生间', value: '有卫生间' },
      ],
      rules: {
        name: [{ required: true, message: '请输入站点名称', trigger: 'blur' }],
        detail_address: [{ required: true, message: '请输入详细地址', trigger: 'blur' }],
        phone: [{ required: true, message: '请输入电话', trigger: 'blur' }],
      },
      reserveDialogVisible: false,
      reserveType: 1,
      currentStationId: null,
      currentStationName: '',
      reserveForm: { amount: 0, remark: '' },
      qrDialogVisible: false,
      qrImageUrl: '',
      qrType: 'qr',
      areaOptions: AREA_OPTIONS,
      cascaderProps: { label: 'name', value: 'name', children: 'children' },
      exportFields: [
        { key: 'id', label: '序号列', checked: true },
        { key: 'name', label: '站点名称', checked: true },
        { key: 'available_balance', label: '可提现金额（元）', checked: true },
        { key: 'reserve', label: '储备金（元）', checked: true },
        { key: 'province', label: '省', checked: true },
        { key: 'city', label: '市', checked: true },
        { key: 'district', label: '区/县', checked: true },
        { key: 'detail_address', label: '详细地址', checked: true },
        { key: 'phone', label: '电话', checked: true },
        { key: 'status_label', label: '状态', checked: true },
        { key: 'created_at_label', label: '创建时间', checked: true },
      ],
      detailDialogVisible: false,
      currentStationRow: null,
      statType: '7days',
      orderList: [],
      totalCount: 0,
      totalAmount: 0,
      superRatio: 0,
      stationRatio: 0,
      shareholderRatio: 0,
      superAmount: '0.00',
      totalSuperAmount: 0,
      stationAmount: '0.00',
      shareholderAmount: '0.00',
      detailLoading: false,
      exportDialogVisible: false,
      splitDialogVisible: false,
      splitSubmitting: false,
      currentStationRowForSplit: null,
      splitForm: {
        type: '0', scene: '00', rule: '1', minRatio: 0.05, maxMerchantCount: 3,
        rate: 0.02, feeBearer: '1', ruleSource: '2', details: [], picList: [], uploadedPics: [],
      },
      mapDialogVisible: false,
      mapInstance: null,
      marker: null,
      geocoder: null,
      tempLng: '',
      tempLat: '',
      tempAddress: '',
      mapLoaded: false,
      searchKeyword: '',
    };
  },
  computed: {
    fileurl() { return this.$store.state.common.systemInfo?.fileurl || ''; },
    uploadHeaders() { return { Authorization: 'Bearer ' + (this.$store.state.user?.token || '') }; },
    reserveDialogTitle() {
      const action = this.reserveType === 1 ? '增加储备金' : '减少储备金';
      return this.currentStationName ? `${this.currentStationName} - ${action}` : action;
    },
    exportApi() { return this.$api.station.export; },
    uploadUrl() {
      const host = this.$store.state.config.host || '';
      const port = this.$store.state.config.port || '';
      return host + (port ? ':' + port : '') + '/upload/upload/image';
    },
    isSuperAdmin() {
      const u = this.$store.state.user.user || {};
      return u.role === 'super_admin' || u.role_id === 1;
    },
    isStationLeader() {
      const u = this.$store.state.user.user || {};
      return u.role === 'station_leader';
    },
  },
  async mounted() {
    if (!this.isSuperAdmin) {
      const u = this.$store.state.user.user || {};
      const myEnterpriseId = u.enterpriseId || u.enterprise_id;
      if (myEnterpriseId) {
        this.searchForm.enterpriseId = myEnterpriseId;
        this.handleEnterpriseChange(myEnterpriseId);
      }
    }
    await this.loadFormEnterprises();
    this.fetchData();
  },
  methods: {
    defaultFileName() { return '站点数据'; },
    formatTime(ts) {
      if (!ts) return '';
      if (ts.toString().length === 10) ts = ts * 1000;
      return moment(ts).format('YYYY-MM-DD HH:mm:ss');
    },
    async loadFormEnterprises() {
      this.enterpriseLoading = true;
      try {
        const ret = await this.$api.enterprise.page({ status: 1, current: 1, pageSize: 999 });
        if (ret && ret.success) {
          const list = ret.data?.data || ret.data?.list || ret.data || [];
          this.formEnterpriseList = list.map(it => ({ ...it, id: Number(it.id) }));
        } else { console.warn('[loadFormEnterprises] 接口返回失败:', ret); }
      } catch (e) { console.error('加载企业列表失败:', e); } 
      finally { this.enterpriseLoading = false; }
    },
    handleSearch() { this.page.current = 1; this.fetchData(); },
    resetSearch() {
      this.searchForm = { enterpriseId: null, stationId: null, dateRange: null };
      this.stationOptions = [];
      this.page.current = 1;
      this.fetchData();
    },
    async fetchData() {
      let startDate = '', endDate = '';
      if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
        startDate = this.searchForm.dateRange[0];
        endDate = this.searchForm.dateRange[1];
      }
      const params = {
        current: this.page.current, pageSize: this.page.pageSize,
        enterpriseId: this.searchForm.enterpriseId || undefined,
        stationId: this.searchForm.stationId || undefined,
        startDate: startDate || undefined, endDate: endDate || undefined,
      };
      try {
        const ret = await this.$api.station.page(params);
        if (ret && ret.success) {
          this.tableData = ret.data.data || [];
          this.total = ret.data.total || 0;
        } else { pub('error_message', ret?.message || '加载数据失败'); }
      } catch (error) { console.error('请求异常:', error); pub('error_message', '请求失败'); }
    },
    onSizeChange() { this.page.current = 1; this.fetchData(); },
    handleRegionChange(value) {
      if (value && value.length > 0) {
        this.form.province = value[0] || '';
        this.form.city = value[1] || '';
        this.form.district = value[2] || '';
      } else { this.form.province = ''; this.form.city = ''; this.form.district = ''; }
    },
    async openAddDialog() {
      this.editId = null;
      this.dialogTitle = '添加站点';
      this.resetForm();
      if (!this.isSuperAdmin) {
        const u = this.$store.state.user.user || {};
        this.form.enterpriseId = u.enterpriseId || u.enterprise_id || null;
      }
      if (!this.formEnterpriseList.length) { await this.loadFormEnterprises(); }
      this.$nextTick(() => { this.$refs.formRef?.clearValidate(); });
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.editId = row.id;
      this.dialogTitle = '编辑站点';
      this.form = {
        name: row.name || '', category: row.category || '', paymentType: row.payment_type || 'all',
        paymentChannel: row.payment_channel || 'yinsheng',
        enterpriseId: row.enterprise_id || null,
        region: [], province: row.province || '', city: row.city || '', district: row.district || '',
        coordinates: { lng: row.longitude || '', lat: row.latitude || '' },
        detail_address: row.detail_address || '', contact: row.contact || '', phone: row.phone || '',
        tags: row.tags ? row.tags.split(',').filter(Boolean) : [],
        logo: row.logo || '', bgImage: row.bg_image || '', remark: row.remark || '',
        oil92: parseInt(row.oil92) || 0, oil95: parseInt(row.oil95) || 0, oil98: parseInt(row.oil98) || 0,
        oil0: parseInt(row.oil0) || 0, commission_rate: parseFloat(row.commission_rate) || 0,
        status: parseInt(row.status) || 0,
      };
      if (row.province) this.form.region.push(row.province);
      if (row.city) this.form.region.push(row.city);
      if (row.district) this.form.region.push(row.district);
      this.$nextTick(() => { this.$refs.formRef?.clearValidate(); });
      this.dialogVisible = true;
    },
    resetForm() {
      this.form = {
        name: '', category: '', paymentType: 'all', paymentChannel: 'yinsheng',
        enterpriseId: null, region: [], province: '', city: '', district: '',
        coordinates: { lng: '', lat: '' }, detail_address: '', contact: '', phone: '',
        tags: [], logo: '', bgImage: '', remark: '', oil92: 0, oil95: 0, oil98: 0, oil0: 0,
        commission_rate: 0, status: 1,
      };
    },
    closeDialog() { this.dialogVisible = false; this.editId = null; },
    submitForm() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        try {
          const postData = {
            name: this.form.name, enterprise_id: this.form.enterpriseId ?? null,
            category: this.form.category || null, payment_type: this.form.paymentType || null,
            payment_channel: this.form.paymentChannel || null, contact: this.form.contact || null,
            logo: this.form.logo || null, bg_image: this.form.bgImage || null, remark: this.form.remark || null,
            province: this.form.province, city: this.form.city, district: this.form.district,
            detail_address: this.form.detail_address, phone: this.form.phone,
            oil92: Number(this.form.oil92) || 0, oil95: Number(this.form.oil95) || 0,
            oil98: Number(this.form.oil98) || 0, oil0: Number(this.form.oil0) || 0,
            commission_rate: Number(this.form.commission_rate) || 0, status: Number(this.form.status) ?? 1,
            longitude: this.form.coordinates?.lng || null, latitude: this.form.coordinates?.lat || null,
            tags: this.form.tags && this.form.tags.length ? this.form.tags.join(',') : '',
          };
          const keepFields = ['enterprise_id'];
          Object.keys(postData).forEach(key => {
            if (keepFields.includes(key)) return;
            if (postData[key] === '' || postData[key] === null || postData[key] === undefined) { delete postData[key]; }
          });
          let ret;
          if (this.editId) { ret = await this.$api.station.put({ id: this.editId, ...postData }); } 
          else { ret = await this.$api.station.post(postData); }
          if (ret && ret.success) {
            pub('success_message', this.editId ? '编辑成功' : '添加成功');
            this.closeDialog();
            this.fetchData();
          } else { pub('error_message', ret?.message || '操作失败'); }
        } catch (error) { console.error('提交失败:', error); pub('error_message', '请求失败，请重试'); }
      });
    },
    handleDelete(row) {
      this.$api.station.del(row.id).then((ret) => {
        if (ret && ret.success) { pub('success_message', '删除成功'); this.fetchData(); } 
        else { pub('error_message', ret?.message || '删除失败'); }
      }).catch(() => { pub('error_message', '请求失败'); });
    },
    async toggleStatus(row) {
      try {
        const ret = await this.$api.station.put({ id: row.id, status: row.status });
        if (ret && ret.success) { pub('success_message', `已${row.status === 1 ? '启用' : '停用'}`); } 
        else { row.status = row.status === 1 ? 0 : 1; pub('error_message', ret?.message || '状态切换失败'); }
      } catch (error) { row.status = row.status === 1 ? 0 : 1; pub('error_message', '请求失败，请重试'); }
    },
    handleReserveCommand(row, command) {
      if (command === 'increase') this.openReserveDialog(row, 1);
      else if (command === 'decrease') this.openReserveDialog(row, 2);
    },
    openReserveDialog(row, type) {
      this.currentStationId = row.id; this.currentStationName = row.name;
      this.reserveType = type; this.reserveForm.amount = 0; this.reserveForm.remark = '';
      this.reserveDialogVisible = true;
    },
    resetReserveDialog() {
      this.reserveForm.amount = 0; this.reserveForm.remark = '';
      this.currentStationId = null; this.currentStationName = '';
    },
    async submitReserve() {
      if (this.reserveForm.amount <= 0) { pub('error_message', '金额必须大于0'); return; }
      const amountInCent = Math.round(this.reserveForm.amount * 100);
      const payload = { stationId: this.currentStationId, type: this.reserveType, amount: amountInCent, remark: this.reserveForm.remark };
      try {
        const ret = await this.$api.station.adjustReserve(payload);
        if (ret && ret.success) { pub('success_message', '储备金调整成功'); this.reserveDialogVisible = false; this.fetchData(); } 
        else { pub('error_message', ret?.message || '调整失败'); }
      } catch (e) { console.error('调整储备金异常:', e); pub('error_message', '请求异常'); }
    },
    async handleQrCode(row) {
      this.qrType = 'qr';
      if (row.wxacode_url) { this.qrImageUrl = this.fileurl + row.wxacode_url.replace(/^\//, ''); this.qrDialogVisible = true; return; }
      try {
        const confirm = await this.$confirm('该站点尚未生成小程序码，是否立即生成？', '提示', { confirmButtonText: '生成', cancelButtonText: '取消', type: 'info' }).catch(() => false);
        if (!confirm) return;
        const ret = await this.$api.station.generateWxCode({ id: row.id });
        if (ret && ret.success) {
          row.wxacode_url = ret.data.wxacode_url; this.$forceUpdate(); this.qrImageUrl = ret.data.wxacode_url; this.qrDialogVisible = true;
          pub('success_message', '小程序码生成成功');
        } else { pub('error_message', ret?.message || '生成小程序码失败'); }
      } catch (e) { console.error('生成小程序码异常', e); pub('error_message', '生成小程序码失败'); }
    },
    async handleAlipayCode(row) {
      this.qrType = 'alipay';
      if (row.alipay_qr) { this.qrImageUrl = this.fileurl + row.alipay_qr.replace(/^\//, ''); this.qrDialogVisible = true; return; }
      try {
        const confirm = await this.$confirm('该站点尚未生成支付宝支付码，是否立即生成？', '提示', { confirmButtonText: '生成', cancelButtonText: '取消', type: 'info' }).catch(() => false);
        if (!confirm) return;
        const ret = await this.$api.station.generateAlipayCode({ id: row.id });
        if (ret && ret.success) {
          row.alipay_qr = ret.data.alipay_qr; this.$forceUpdate(); this.qrImageUrl = ret.data.alipay_qr; this.qrDialogVisible = true;
          pub('success_message', '支付宝支付码生成成功');
        } else { pub('error_message', ret?.message || '生成支付宝支付码失败'); }
      } catch (e) { console.error('生成支付宝支付码异常', e); pub('error_message', '生成支付宝支付码失败'); }
    },
    async handleUnionCode(row) {
      this.qrType = 'union';
      if (row.union_code) { this.qrImageUrl = this.fileurl + row.union_code.replace(/^\//, ''); this.qrDialogVisible = true; return; }
      try {
        const confirm = await this.$confirm('该站点尚未生成聚合码，是否立即生成？', '提示', { confirmButtonText: '生成', cancelButtonText: '取消', type: 'info' }).catch(() => false);
        if (!confirm) return;
        const ret = await this.$api.station.generateUnionCode({ id: row.id });
        if (ret && ret.success) {
          row.union_code = ret.data.union_code; this.$forceUpdate(); this.qrImageUrl = this.fileurl + ret.data.union_code.replace(/^\//, ''); this.qrDialogVisible = true;
          this.$message.success('聚合码生成成功');
        } else { this.$message.error(ret?.message || '生成聚合码失败'); }
      } catch (e) { console.error('生成聚合码异常', e); this.$message.error('生成聚合码失败'); }
    },
    openDetailDialog(row) {
      this.currentStationRow = row; this.detailDialogVisible = true; this.statType = '7days'; this.fetchDetailData();
    },
    async fetchDetailData() {
      if (!this.currentStationRow) return;
      this.detailLoading = true;
      try {
        const params = { stationId: this.currentStationRow.id, type: this.statType };
        const ret = await this.$api.station.orderStatistics(params);
        if (ret && ret.success) {
          const data = ret.data || {};
          this.orderList = data.orders || [];
          this.totalCount = data.total_count || 0;
          this.totalAmount = data.total_amount || 0;
          this.superRatio = data.ratios?.super_admin_ratio || 0;
          this.totalSuperAmount = data.total_super_amount || 0;
          this.superAmount = this.totalSuperAmount.toFixed(2);
        } else { pub('error_message', ret?.message || '获取明细失败'); }
      } catch (e) { console.error('获取明细异常:', e); pub('error_message', '请求异常'); } 
      finally { this.detailLoading = false; }
    },
    handleStatTypeChange() { this.fetchDetailData(); },
    openSplitDialog(row) {
      this.currentStationRowForSplit = row;
      this.splitForm = {
        type: '0', scene: '00', rule: '1', minRatio: 0.05, maxMerchantCount: 3,
        rate: 0.02, feeBearer: '1', ruleSource: '2', details: [], picList: [], uploadedPics: [],
      };
      this.splitDialogVisible = true;
    },
    onTypeChange() { this.splitForm.rule = '1'; this.splitForm.details = []; },
    onRuleChange() { this.splitForm.details = []; },
    addDetailRow() {
      if (this.splitForm.rule === '2') { this.splitForm.details.push({ divMercId: '', ratio: 0, feeFlag: undefined }); } 
      else if (this.splitForm.rule === '3') { this.splitForm.details.push({ divMercId: '', amount: 0, feeFlag: undefined }); }
    },
    removeDetailRow(index) { this.splitForm.details.splice(index, 1); },
    async handleSplitUpload(file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        try {
          const res = await this.$api.station.uploadSplitPic({ picType: this.splitForm.scene, picBase64: base64 });
          if (res.success) {
            this.splitForm.uploadedPics.push({ picType: this.splitForm.scene, picUrl: res.data.picUrl });
            this.$message.success('图片上传成功');
          }
        } catch (e) { this.$message.error('图片上传失败'); }
      };
      reader.readAsDataURL(file.raw);
    },
    async submitSplit() {
      if (!this.splitForm.scene) { this.$message.warning('请选择分账场景'); return; }
      if (this.splitForm.type === '1') {
        if (!this.splitForm.rule) { this.$message.warning('请选择分账规则'); return; }
        if (this.splitForm.rule === '1') {
          if (this.splitForm.minRatio === undefined || this.splitForm.maxMerchantCount === undefined) { this.$message.warning('请填写完整的最低分账比例和参与分账商户数'); return; }
        } else {
          if (this.splitForm.details.length === 0) { this.$message.warning('请添加分账明细'); return; }
          const feeOnes = this.splitForm.details.filter(d => d.feeFlag === '1');
          if (feeOnes.length > 1) { this.$message.warning('手续费承担方只能有一个'); return; }
          if (this.splitForm.rule === '2') {
            const totalRatio = this.splitForm.details.reduce((sum, d) => sum + (d.ratio || 0), 0);
            if (Math.abs(totalRatio - 100) > 0.01) { this.$message.warning(`比例之和必须为100%，当前为${totalRatio.toFixed(2)}%`); return; }
          }
        }
      } else if (this.splitForm.type === '0') {
        if (this.splitForm.rate === undefined) { this.$message.warning('请填写分账费率'); return; }
      } else { this.$message.warning('请选择有效的分账类型'); return; }

      const payload = { stationId: this.currentStationRowForSplit.id, type: this.splitForm.type, scene: this.splitForm.scene, pics: this.splitForm.uploadedPics };
      if (this.splitForm.type === '1') {
        payload.rule = this.splitForm.rule;
        if (this.splitForm.rule === '1') { payload.minRatio = this.splitForm.minRatio; payload.maxMerchantCount = this.splitForm.maxMerchantCount; } 
        else {
          payload.details = this.splitForm.details.map(d => {
            const item = { divMercId: d.divMercId };
            if (this.splitForm.rule === '2') item.ratio = d.ratio; else item.amount = d.amount;
            if (d.feeFlag !== undefined) item.feeFlag = d.feeFlag;
            return item;
          });
        }
      } else { payload.ratio = this.splitForm.rate; payload.feeBearer = this.splitForm.feeBearer; payload.ruleSource = this.splitForm.ruleSource; }

      this.splitSubmitting = true;
      try {
        const ret = await this.$api.station.openSplit(payload);
        if (ret.success) { this.$message.success('分账申请提交成功，等待银盛审核和签约'); this.splitDialogVisible = false; } 
        else { this.$message.error(ret.message || '提交失败'); }
      } catch (e) { console.error('分账提交异常:', e); this.$message.error('提交异常，请稍后重试'); } 
      finally { this.splitSubmitting = false; }
    },
    handleLogoSuccess(res) {
      if (!res || !res.success) { pub('error_message', res?.message || 'LOGO上传失败'); return; }
      const path = typeof res.data === 'string' ? res.data : (res.data?.url || res.data?.filepath || res.data?.path || '');
      if (!path) { pub('error_message', '上传成功但未返回文件路径'); return; }
      this.form.logo = path;
    },
    handleBgSuccess(res) {
      if (!res || !res.success) { pub('error_message', res?.message || '背景图上传失败'); return; }
      const path = typeof res.data === 'string' ? res.data : (res.data?.url || res.data?.filepath || res.data?.path || '');
      if (!path) { pub('error_message', '上传成功但未返回文件路径'); return; }
      this.form.bgImage = path;
    },
    handleExport() { this.openExportDialog(); },
    handlePrint() { window.print(); },
    getSearchParams() {
      const params = { ...this.searchForm };
      if (params.dateRange && params.dateRange.length === 2) {
        params.startDate = params.dateRange[0]; params.endDate = params.dateRange[1];
        delete params.dateRange;
      }
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') { delete params[key]; }
      });
      return params;
    },
    openMapSelector() {
      if (this.form.coordinates.lng && this.form.coordinates.lat) {
        this.tempLng = this.form.coordinates.lng; this.tempLat = this.form.coordinates.lat; this.tempAddress = this.form.detail_address || '';
      } else { this.tempLng = ''; this.tempLat = ''; this.tempAddress = ''; }
      this.searchKeyword = ''; this.mapDialogVisible = true;
      this.$nextTick(() => { this.initMap(); });
    },
    async initMap() {
      if (!window.AMap) { await this.loadAMapScript(); }
      const container = document.getElementById('mapContainer');
      if (!container) return;
      const map = new window.AMap.Map('mapContainer', { zoom: 15, center: [116.397428, 39.90923], viewMode: '2D' });
      this.mapInstance = map;
      window.AMap.plugin(['AMap.ToolBar', 'AMap.Scale', 'AMap.MapType'], () => {
        map.addControl(new window.AMap.ToolBar({ position: 'RT' }));
        map.addControl(new window.AMap.Scale());
      });
      window.AMap.plugin(['AMap.Geocoder'], () => { this.geocoder = new window.AMap.Geocoder({ city: '全国', radius: 1000 }); });
      if (this.tempLng && this.tempLat) {
        const lngLat = [parseFloat(this.tempLng), parseFloat(this.tempLat)];
        map.setCenter(lngLat); this.addMarker(lngLat);
      }
      map.on('click', (e) => {
        const lnglat = e.lnglat; const lng = lnglat.getLng(); const lat = lnglat.getLat();
        this.tempLng = lng.toFixed(6); this.tempLat = lat.toFixed(6);
        this.addMarker([lng, lat]); this.regeocode(lng, lat);
      });
    },
    async loadAMapScript() {
      if (window.AMap) { this.mapLoaded = true; return; }
      let amapKey = this._amapKey; let amapSecurityCode = this._amapSecurityCode;
      if (!amapKey || !amapSecurityCode) {
        try {
          const ret = await this.$api.common.getsysdata();
          const data = ret?.data?.data || ret?.data || ret || {};
          amapKey = data.amap_key; amapSecurityCode = data.amap_security_code;
          this._amapKey = amapKey; this._amapSecurityCode = amapSecurityCode;
        } catch (e) { console.error('[loadAMapScript] 请求 getSystemInfo 失败:', e); }
      }
      if (!amapKey) throw new Error('未配置高德地图 Key');
      if (!amapSecurityCode) throw new Error('未配置高德地图安全密钥');
      window._AMapSecurityConfig = { securityJsCode: amapSecurityCode };
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://webapi.amap.com/maps?v=2.0&key=${amapKey}&plugin=AMap.ToolBar,AMap.Scale,AMap.MapType,AMap.Geocoder,AMap.PlaceSearch&callback=onAMapLoaded`;
        script.async = true;
        window.onAMapLoaded = () => { this.mapLoaded = true; resolve(); };
        script.onerror = () => reject(new Error('高德地图脚本加载失败'));
        document.head.appendChild(script);
      });
    },
    addMarker(lnglat) {
      if (this.marker) { this.marker.setPosition(lnglat); } 
      else { this.marker = new window.AMap.Marker({ position: lnglat, draggable: false }); this.marker.setMap(this.mapInstance); }
    },
    regeocode(lng, lat) {
      if (!this.geocoder) return;
      this.geocoder.getAddress([lng, lat], (status, result) => {
        if (status === 'complete' && result.info === 'OK') { this.tempAddress = result.regeocode.formattedAddress || ''; }
      });
    },
    searchPlace() {
      if (!this.searchKeyword.trim()) { this.$message.warning('请输入搜索关键词'); return; }
      if (!window.AMap) { this.$message.error('地图未加载'); return; }
      const keyword = this.searchKeyword.trim();
      window.AMap.plugin(['AMap.PlaceSearch'], () => {
        const placeSearch = new window.AMap.PlaceSearch({ city: '全国', citylimit: false, pageSize: 10, pageIndex: 1, type: '', extensions: 'all' });
        placeSearch.search(keyword, (status, result) => {
          if (status === 'complete' && result.poiList && result.poiList.pois.length > 0) {
            const pois = result.poiList.pois;
            const preferred = pois.find(p => /服务区|停车区|加油站|加气站/.test(p.name || ''));
            const poi = preferred || pois[0];
            const lng = poi.location.getLng(); const lat = poi.location.getLat();
            this.tempLng = lng.toFixed(6); this.tempLat = lat.toFixed(6); this.tempAddress = poi.address || poi.name || '';
            this.mapInstance.setCenter([lng, lat]); this.addMarker([lng, lat]); this.mapInstance.setZoom(16);
            this.$message.success(`已定位到：${poi.name}`);
          } else { this.$message.warning('未找到该地点，请尝试更换关键词，或直接在地图上点击选点'); }
        });
      });
    },
    resetMapPosition() {
      if (this.tempLng && this.tempLat) {
        const lngLat = [parseFloat(this.tempLng), parseFloat(this.tempLat)];
        this.mapInstance.setCenter(lngLat); this.addMarker(lngLat);
      } else {
        this.mapInstance.setCenter([116.397428, 39.90923]);
        if (this.marker) { this.marker.setMap(null); this.marker = null; }
      }
    },
    confirmMapSelection() {
      if (!this.tempLng || !this.tempLat) { this.$message.warning('请先在地图上选择位置'); return; }
      this.form.coordinates.lng = this.tempLng; this.form.coordinates.lat = this.tempLat;
      if (this.tempAddress) { this.form.detail_address = this.tempAddress; }
      this.mapDialogVisible = false;
    },
    closeMapDialog() {
      if (this.mapInstance) {
        this.mapInstance.destroy(); this.mapInstance = null; this.marker = null; this.geocoder = null;
      }
    },
  },
};
</script>

<style scoped>
/* ★ 核心高度控制 */
.flex-page-layout { display: flex; flex-direction: column; height: calc(100vh - 120px); box-sizing: border-box; }
.search-card { flex-shrink: 0; margin-bottom: 16px; }
.table-card { flex: 1; display: flex; flex-direction: column; overflow: hidden; margin-bottom: 0; }
.table-card :deep(.el-card__body) { flex: 1; display: flex; flex-direction: column; overflow: hidden; padding: 16px; }
.table-container { flex: 1; overflow: hidden; }
.pagination-wrapper { flex-shrink: 0; margin-top: 16px; display: flex; justify-content: center; }

.table-card :deep(.el-card__header) { padding: 10px 16px; border-bottom: 1px solid #e4e7ed; }
.table-header { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.table-header-left { display: flex; align-items: center; gap: 8px; }
.table-header-right { display: flex; align-items: center; gap: 4px; }
.reserve-cell { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: nowrap; }
.reserve-amount { font-size: 15px; font-weight: 400; color: #303133; }
.reserve-amount.negative { color: #f56c6c; }
.reserve-dropdown-trigger { cursor: pointer; color: #409EFF; font-size: 13px; display: inline-flex; align-items: center; gap: 2px; white-space: nowrap; user-select: none; padding: 4px 6px; border-radius: 4px; transition: background-color 0.2s; }
.reserve-dropdown-trigger:hover { background-color: #ecf5ff; color: #66b1ff; }
.reserve-dropdown-trigger:hover svg polyline { stroke: #66b1ff; }
.avatar-uploader :deep(.el-upload) { border: 1px dashed #d9d9d9; border-radius: 6px; cursor: pointer; position: relative; overflow: hidden; width: 100px; height: 100px; display: flex; align-items: center; justify-content: center; }
.avatar-uploader :deep(.el-upload:hover) { border-color: #409EFF; }
.avatar-uploader-icon { font-size: 28px; color: #8c939d; width: 100px; height: 100px; line-height: 100px; text-align: center; }
.avatar { width: 100px; height: 100px; display: block; object-fit: cover; }
</style>